'use client';

import {useContext, useEffect, useRef, useMemo, useState} from 'react';
import amplitudeService from '@features/analytics/amplitudeService';
import {Link, useNavigate, useLocation} from '@tanstack/react-router';
import {useUpdateCSSVariables} from '@features/tools/toolsHook';
import {useViewport} from '@features/flow/components/useViewport';
import {Canvas} from '@features/flow/components/Canvas';
import {LoaderFullScreen} from '@features/ui/components/LoaderFullScreen';
import {FlowNotFoundScreen} from '@features/ui/components/FlowNotFoundScreen';
import {FlowContext, useFlowContextValue} from '@features/contexts/FlowContext';
import {trpc} from '@features/utils/trpc';
import {Button} from '@features/ui/components/Button';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import {useBananiButtonClick} from '@features/flow/[id]/useBananiButtonClick';
import {useActiveScreenId} from '@features/activeScreenIdContext/useActiveScreenId';
import {useSetActiveScreenId} from '@features/activeScreenIdContext/useSetActiveScreenId';
import {ScreenCreationProvider} from '@features/flow/hooks/ScreenCreationContext';
import {PaywallModalProvider} from '@features/subscription/PaywallModal';
import {useSetAtom} from 'jotai';
import {
  interactionModeAtom,
  InteractionModeToggle,
} from '@features/flow/components/InteractionMode';
import {useSession} from '@features/auth/authClient';
import {toast} from 'sonner';
import {ViewerToolbar} from '../components/ViewerToolbar';
import {useFlowsScreens} from '@features/flow/hooks/useFlowScreens';
import {SHARE_KEYS} from '@features/flow/utils/componentScreenId';
import {DinamoFontFaces} from '@features/admin/taste/DinamoFontFaces';

function Chat({id: flowId}: {id: string}) {
  const {searchStr} = useLocation();
  const [landedWithScreen] = useState(() => {
    const sp = new URLSearchParams(searchStr);
    return SHARE_KEYS.some((k) => sp.get(k));
  });
  const activeScreenId = useActiveScreenId();

  const setChatId = useSetActiveScreenId();

  const screensData = useFlowsScreens(flowId).data?.filter(
    (s) => s.type !== 'component',
  );
  const chatIds = useMemo(() => screensData?.map((s) => s.id), [screensData]);

  const {data: flow, error} = trpc.flow.getFlow.useQuery({flowId});
  const {isAuthenticated} = useSession();

  // Sync workspace state for authenticated users so MCP can find this flow
  const updateWorkspace = trpc.workspace.updateWorkspaceState.useMutation();
  useEffect(() => {
    if (!isAuthenticated) return;
    updateWorkspace.mutate({lastOpenFlowId: flowId});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, [flowId, isAuthenticated]);

  const pageRendered = useRef(false);
  useEffect(() => {
    if (pageRendered.current) return;
    pageRendered.current = true;
    amplitudeService.logEvent('[Preview] page rendered', {
      flowId,
      activeScreenId,
    });
  }, [flowId, activeScreenId]);

  useEffect(() => {
    if (!chatIds?.length) return;
    if (activeScreenId && chatIds.includes(activeScreenId)) return;
    setChatId(chatIds[0]);
  }, [chatIds, setChatId, activeScreenId]);

  useUpdateCSSVariables(flowId);

  const flowContextValue = useFlowContextValue(flow);

  if (error?.data?.code === 'NOT_FOUND') {
    return <FlowNotFoundScreen variant='preview' />;
  }
  if (!flow || !flowContextValue) {
    return <LoaderFullScreen />;
  }

  return (
    <FlowContext.Provider value={flowContextValue}>
      <ReadonlyContext.Provider value={PREVIEW_READONLY_CONTEXT_VALUE}>
        <PaywallModalProvider>
          <ScreenCreationProvider flowId={flow.id}>
            <DinamoFontFaces flowId={flowId} />
            <PreviewContent zoomToScreen={landedWithScreen} />
          </ScreenCreationProvider>
        </PaywallModalProvider>
      </ReadonlyContext.Provider>
    </FlowContext.Provider>
  );
}

const PREVIEW_READONLY_CONTEXT_VALUE = {
  readonly: true,
  reason: {
    title: 'Preview mode',
    description: 'This is a preview of the design. Editing is not available.',
  },
} as const;

function PreviewContent({zoomToScreen}: {zoomToScreen: boolean}) {
  useBananiButtonClick();

  const chatId = useActiveScreenId();

  const {flow} = useContext(FlowContext);
  const {isAuthenticated} = useSession();
  const {data: hasComponentsAccess} = trpc.util.hasComponentsAccess.useQuery(
    undefined,
    {enabled: isAuthenticated},
  );

  // Authenticated users get select mode, unauthenticated get pan mode
  const setInteractionMode = useSetAtom(interactionModeAtom);
  useEffect(() => {
    setInteractionMode(isAuthenticated ? 'select' : 'pan');
  }, [setInteractionMode, isAuthenticated]);
  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();
  const duplicateFlow = trpc.flow.duplicateFlow.useMutation({
    onSuccess: (newFlowId) => {
      void trpcUtils.flow.getVisibleFlows.invalidate();
      void navigate({to: `/flow/${newFlowId}`});
    },
  });

  const handleDuplicate = async () => {
    amplitudeService.logEvent('[Preview] duplicate click', {
      chatId,
      flowId: flow.id,
    });
    const toastId = toast.loading('Duplicating to your projects');
    try {
      await duplicateFlow.mutateAsync({flowId: flow.id});
    } finally {
      toast.dismiss(toastId);
    }
  };

  const accessLevel = isAuthenticated ? 'viewer' : 'unauthenticated';

  return (
    <div className='flex h-screen'>
      <ViewerToolbar
        projectName={flow.projectName ?? ''}
        accessLevel={accessLevel}
      />
      <div className='shadow-ui-sm absolute left-1/2 top-1 z-40 -translate-x-1/2 rounded-2xl bg-white p-2'>
        <InteractionModeToggle />
      </div>
      <div className='shadow-ui-sm absolute right-3 top-1 z-40 rounded-2xl bg-white p-2'>
        {isAuthenticated &&
        !(flow.genType === 'components' && !hasComponentsAccess) ? (
          <Button
            size='sm'
            onClick={() => void handleDuplicate()}
            disabled={duplicateFlow.isPending}
          >
            Duplicate
          </Button>
        ) : !isAuthenticated ? (
          <Button asChild size='sm'>
            <Link
              to='/auth/signup'
              target='_blank'
              onClick={() => {
                amplitudeService.logEvent('[Preview] try banani click', {
                  chatId,
                });
              }}
            >
              Try Banani
            </Link>
          </Button>
        ) : null}
      </div>

      <Canvas />
      {zoomToScreen && chatId && <ZoomToScreenOnMount screenId={chatId} />}
    </div>
  );
}

function ZoomToScreenOnMount({screenId}: {screenId: string}) {
  const {fitViewToChat} = useViewport();
  const hasZoomed = useRef(false);

  useEffect(() => {
    if (hasZoomed.current) return;
    hasZoomed.current = true;

    // Small delay to ensure ReactFlow is fully initialized
    const timer = setTimeout(() => {
      void fitViewToChat(screenId, {duration: 300});
    }, 100);

    return () => clearTimeout(timer);
  }, [screenId, fitViewToChat]);

  return null;
}

export default Chat;
